/**
 * EdgeRenderingServiceAdapter
 *
 * E018-S009: Adapter that implements IEdgeRenderingService using
 * the existing EdgeRenderingService with lazy path calculation.
 */

import { Bound } from '@blocksuite/affine/global/gfx';
import type { GfxController, GfxModel } from '@blocksuite/std/gfx';

import type { IEdgeRenderingService } from '../core/interfaces/IEdgeRenderingService';
import type { EdgeDefinition, EdgeType } from '../core/types';
import { isAutomationEdgeMetadata } from '../core/types';
import {
  type EdgeBoundEndpoint,
  EdgeRenderingService,
} from '../rendering/edge-rendering-service';
import {
  type EdgeTargetOverlay,
  SVGEdgeRenderer,
} from '../rendering/svg-edge-renderer';
import { createPathStrategy } from '../strategies';
import { DirectRoutingStrategy } from '../strategies/routing/direct-routing';
import type { EdgeVisualType, StreamlainEdge, ViewportState } from '../types';
import { loadEdgeSettings } from '../utils/config-persistence';

// =============================================================================
// Implementation
// =============================================================================

type GfxLayerWithCanvasElements = GfxController['layer'] & {
  canvasElements?: Iterable<GfxModel>;
};

type GfxModelWithModel = GfxModel & {
  model?: unknown;
};

/** Radius of the SVG source dot in model units. */
const DATABASE_SOURCE_HANDLE_OUTSET = 7;

/**
 * Adapter that wraps EdgeRenderingService with IEdgeRenderingService interface.
 * Implements LAZY path calculation - paths computed at render time.
 */
export class EdgeRenderingServiceAdapter implements IEdgeRenderingService {
  private readonly service: EdgeRenderingService;
  private readonly automationPortObservers = new Map<
    string,
    { element: Element; observer: ResizeObserver }
  >();
  private mounted = false;

  constructor(private readonly gfx: GfxController) {
    // Load persisted settings (localStorage) for path strategy
    const { pathType } = loadEdgeSettings();
    const pathStrategy = createPathStrategy(pathType);
    const automationPathStrategy = createPathStrategy('rounded-elbow');
    const routingStrategy = new DirectRoutingStrategy();
    const renderer = new SVGEdgeRenderer(
      edge => this.getEdgeZIndex(edge),
      edge => this.getAutomationPortOverlay(edge)
    );

    // Create service with bound getter
    this.service = new EdgeRenderingService(
      renderer,
      pathStrategy,
      routingStrategy,
      (id, edge, endpoint) => this.getBound(id, edge, endpoint),
      () => this.getAllElements(),
      { automation: automationPathStrategy }
    );
  }

  /**
   * Gets bound for an element.
   */
  private getBound(
    elementId: string,
    edge?: StreamlainEdge,
    endpoint?: EdgeBoundEndpoint
  ): Bound | null {
    const indexed = this.gfx.getElementById(elementId) as GfxModel | null;
    const indexedBound = this.extractBound(indexed);
    if (indexedBound) {
      return this.resolveEndpointBound(elementId, indexedBound, edge, endpoint);
    }

    const layer = this.gfx.layer as GfxLayerWithCanvasElements;
    for (const element of layer.blocks) {
      if (element.id !== elementId) continue;
      const bound = this.extractBound(element);
      if (bound) {
        return this.resolveEndpointBound(elementId, bound, edge, endpoint);
      }
    }

    if (layer.canvasElements) {
      for (const element of layer.canvasElements) {
        if (element.id !== elementId) continue;
        const bound = this.extractBound(element);
        if (bound) {
          return this.resolveEndpointBound(elementId, bound, edge, endpoint);
        }
      }
    }

    return null;
  }

  /**
   * Automation paths meet their semantic stage. The renderer exposes only the
   * part inside the automation in its clipped foreground layer.
   */
  private resolveEndpointBound(
    elementId: string,
    outerBound: Bound,
    edge?: StreamlainEdge,
    endpoint?: EdgeBoundEndpoint
  ): Bound {
    const metadata = edge?.metadata;
    if (
      !edge ||
      !metadata ||
      !isAutomationEdgeMetadata(metadata) ||
      endpoint === 'source-container' ||
      endpoint === 'target-container'
    ) {
      return outerBound;
    }

    const direction = metadata.direction;
    if (direction === 'input' && endpoint === 'source') {
      // Keep the entire database source dot outside the card while the edge
      // itself remains on the normal behind-block layer. The dot touches the
      // database border instead of being centered underneath it.
      return new Bound(
        outerBound.x,
        outerBound.y,
        outerBound.w + DATABASE_SOURCE_HANDLE_OUTSET,
        outerBound.h
      );
    }
    const role =
      direction === 'input' && endpoint === 'target'
        ? 'input'
        : direction === 'output' && endpoint === 'source'
          ? 'output'
          : null;
    if (!role) return outerBound;

    const block = this.gfx.std?.view.getBlock(elementId) as HTMLElement | null;
    const stage = block?.querySelector<HTMLElement>(`[data-role="${role}"]`);
    if (!block || !stage) return outerBound;

    this.observeAutomationPort(elementId, role, stage);
    const blockRect = block.getBoundingClientRect();
    const stageRect = stage.getBoundingClientRect();
    if (blockRect.height <= 0 || stageRect.height <= 0) return outerBound;

    const relativeLeft = (stageRect.left - blockRect.left) / blockRect.width;
    const relativeTop = (stageRect.top - blockRect.top) / blockRect.height;
    return new Bound(
      outerBound.x + outerBound.w * relativeLeft,
      outerBound.y + outerBound.h * relativeTop,
      outerBound.w * (stageRect.width / blockRect.width),
      outerBound.h * (stageRect.height / blockRect.height)
    );
  }

  private getAutomationPortOverlay(
    edge: StreamlainEdge
  ): EdgeTargetOverlay | null {
    const metadata = edge.metadata;
    if (!metadata || !isAutomationEdgeMetadata(metadata)) return null;
    const automationId = metadata.automationId;
    const automation = this.findLayerElement(automationId);
    const automationBound = this.extractBound(automation);
    if (!automation || !automationBound) return null;
    const layer = this.gfx.layer as GfxLayerWithCanvasElements;
    return {
      clipBound: {
        x: automationBound.x,
        y: automationBound.y,
        w: automationBound.w,
        h: automationBound.h,
      },
      endpoint: metadata.direction === 'input' ? 'target' : 'source',
      stackingElement: this.gfx.std?.view.getBlock(automationId),
      zIndex: layer.getZIndex(automation),
    };
  }

  private observeAutomationPort(
    elementId: string,
    role: 'input' | 'output',
    stage: HTMLElement
  ): void {
    if (typeof ResizeObserver === 'undefined') return;
    const key = `${elementId}:${role}`;
    const current = this.automationPortObservers.get(key);
    if (current?.element === stage) return;
    current?.observer.disconnect();
    const observer = new ResizeObserver(() => this.service.forceRender());
    observer.observe(stage);
    this.automationPortObservers.set(key, { element: stage, observer });
  }

  /** Keeps an edge immediately behind the lower of its connected elements. */
  private getEdgeZIndex(edge: StreamlainEdge): number {
    const layer = this.gfx.layer as GfxLayerWithCanvasElements;
    const endpointZIndexes = [edge.sourceId, edge.targetId]
      .map(id => this.findLayerElement(id))
      .filter((element): element is GfxModel => element !== null)
      .map(element => layer.getZIndex(element))
      .filter(zIndex => zIndex > 0);

    if (endpointZIndexes.length === 0) return 0;
    return Math.max(0, Math.min(...endpointZIndexes) - 1);
  }

  private findLayerElement(elementId: string): GfxModel | null {
    const layer = this.gfx.layer as GfxLayerWithCanvasElements;
    for (const element of layer.blocks) {
      if (element.id === elementId) return element;
    }
    if (layer.canvasElements) {
      for (const element of layer.canvasElements) {
        if (element.id === elementId) return element;
      }
    }
    return null;
  }

  private extractBound(element: GfxModel | null): Bound | null {
    if (element && 'elementBound' in element) {
      return element.elementBound as Bound;
    }

    const model = (element as GfxModelWithModel | null)?.model;
    if (model && typeof model === 'object' && 'elementBound' in model) {
      return model.elementBound as Bound;
    }

    return null;
  }

  /**
   * Gets all elements.
   */
  private getAllElements(): GfxModel[] {
    return Array.from(this.gfx.layer.blocks);
  }

  // ===========================================================================
  // IEdgeRenderingService Implementation
  // ===========================================================================

  mount(container: HTMLElement): void {
    this.service.mount(container);
    this.mounted = true;
  }

  unmount(): void {
    for (const { observer } of this.automationPortObservers.values()) {
      observer.disconnect();
    }
    this.automationPortObservers.clear();
    this.service.unmount();
    this.mounted = false;
  }

  isMounted(): boolean {
    return this.mounted;
  }

  addEdge(definition: EdgeDefinition): StreamlainEdge {
    // Note: Path is NOT calculated here - it's lazy at render time
    // CRITICAL: Pass definition.id to enable sync with EdgeSyncService
    return this.service.addEdge(
      definition.sourceId,
      definition.targetId,
      definition.type,
      {
        id: definition.id,
        sourceAnchor: definition.sourceAnchor,
        targetAnchor: definition.targetAnchor,
        visualType: this.deriveVisualType(definition),
        metadata: definition.metadata,
        sourceMarker:
          definition.type === 'automation' &&
          isAutomationEdgeMetadata(definition.metadata)
            ? 'dot'
            : undefined,
        targetMarker:
          definition.type === 'automation' &&
          isAutomationEdgeMetadata(definition.metadata)
            ? 'arrow'
            : undefined,
      }
    );
  }

  /**
   * Derives visual edge type from definition metadata.
   * Automation edges split into input (blue) / output (green).
   */
  private deriveVisualType(definition: EdgeDefinition): EdgeVisualType {
    if (definition.type === 'relation') return 'relation';
    if (isAutomationEdgeMetadata(definition.metadata)) {
      return definition.metadata.direction === 'input'
        ? 'automation-input'
        : 'automation-output';
    }
    return 'automation-output'; // fallback for automation without metadata
  }

  removeEdge(id: string): void {
    this.service.removeEdge(id);
  }

  getEdge(id: string): StreamlainEdge | undefined {
    return this.service.getEdge(id);
  }

  getAllEdges(): StreamlainEdge[] {
    return this.service.getAllEdges();
  }

  getEdgesByType(type: EdgeType): StreamlainEdge[] {
    return this.service.getEdgesByType(type);
  }

  updateViewport(viewport: ViewportState): void {
    this.service.updateViewport(viewport);
  }

  render(): void {
    this.service.forceRender();
  }

  setSystemEnabled(type: EdgeType, enabled: boolean): void {
    this.service.setSystemEnabled(type, enabled);
  }

  setSystemOpacity(type: EdgeType, opacity: number): void {
    this.service.setSystemOpacity(type, opacity);
  }

  setEdgeHighlight(edgeId: string, highlighted: boolean): void {
    this.service.setEdgeHighlight(edgeId, highlighted);
  }

  highlightEdgesForElement(elementId: string, highlighted: boolean): void {
    this.service.highlightEdgesForElement(elementId, highlighted);
  }

  clearAllHighlights(): void {
    this.service.clearAllHighlights();
  }

  getEdgesForElement(elementId: string): StreamlainEdge[] {
    return this.service.getEdgesForElement(elementId);
  }

  /**
   * Gets the internal EdgeRenderingService for use by EdgeHighlightService.
   * This is needed because EdgeHighlightService requires the full API.
   */
  getInternalService(): EdgeRenderingService {
    return this.service;
  }
}
